import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth, db, COLLECTIONS } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// פונקציה לשמירת נתוני משתמש ב-localStorage
const saveUserToLocalStorage = (userData) => {
  if (userData) {
    localStorage.setItem('userRole', userData.role || '');
    localStorage.setItem('userDepartmentId', userData.departmentId || '');
    localStorage.setItem('userTeamId', userData.teamId || '');
    localStorage.setItem('userName', userData.name || '');
    localStorage.setItem('userEmail', userData.email || '');
    }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Login function
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Try to get additional user data from Firestore
      let userData = {
        id: userCredential.user.uid,
        email: userCredential.user.email,
        name: userCredential.user.displayName || userCredential.user.email.split('@')[0],
        role: 'employee',   // ברירת מחדל לעובד
        departmentId: ''
      };
      
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userCredential.user.uid));
        if (userDoc.exists()) {
          userData = {
            ...userData,
            ...userDoc.data()
          };
          // שמור ב-localStorage אחרי התחברות מוצלחת
          saveUserToLocalStorage(userData);
        }
      } catch (firestoreError) {
        // Could not load user data from Firestore, using defaults
      }
      
      setCurrentUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      // נקה את ה-localStorage בעת התנתקות
      localStorage.removeItem('userRole');
      localStorage.removeItem('userDepartmentId');
      localStorage.removeItem('userTeamId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
    } catch (error) {
      // Logout error
    }
  };

  // Check permissions
  const hasPermission = (requiredRole) => {
    if (!currentUser) return false;
    
    const roleHierarchy = {
      'org_admin': 6,     // מנהל ראשי - הרמה הגבוהה ביותר
      'ceo': 5,           // מנכ"ל
      'deputy_ceo': 4,    // סמנכ"ל
      'dept_manager': 3,  // מנהל מחלקה
      'team_manager': 2,  // מנהל צוות
      'employee': 1       // עובד - הרמה הנמוכה ביותר
    };
    
    return roleHierarchy[currentUser.role] >= roleHierarchy[requiredRole];
  };

  // Update current user data
  const updateCurrentUser = async (updates) => {
    try {
      // Update in Firestore
      await updateDoc(doc(db, COLLECTIONS.USERS, currentUser.id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      const updatedUser = { ...currentUser, ...updates };
      setCurrentUser(updatedUser);
      
      // עדכן גם ב-localStorage
      saveUserToLocalStorage(updatedUser);
      
    } catch (error) {
      console.error('Error updating current user:', error);
    }
  };

  // Update current user data locally (without Firebase)
  const updateCurrentUserLocal = (updates) => {
    const updatedUser = { ...currentUser, ...updates };
    setCurrentUser(updatedUser);
    // עדכן גם ב-localStorage
    saveUserToLocalStorage(updatedUser);
  };

  // Refresh current user data from Firestore
  const refreshCurrentUser = async () => {
    if (!currentUser?.id) return;
    
    try {
      console.log('Refreshing current user data...');
      
      // Try multiple approaches to find user data
      let userData = null;
      
      // Method 1: Try by email (formatted)
      const userDocId = currentUser.email.replace(/[.@]/g, '_');
      try {
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userDocId));
        if (userDoc.exists()) {
          userData = {
            id: userDocId, // Use the document ID from Firestore
            email: currentUser.email,
            ...userDoc.data()
          };
        }
      } catch (error) {
        console.log('Error fetching by email:', error);
      }
      
      // Method 2: Try by UID if email didn't work
      if (!userData) {
        try {
          const userDocByUid = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.id));
          if (userDocByUid.exists()) {
            userData = {
              id: currentUser.id, // Use the current ID
              email: currentUser.email,
              ...userDocByUid.data()
            };
            console.log('Found user data by UID:', userData);
          }
        } catch (error) {
          console.log('Error fetching by UID:', error);
        }
      }
      
      // Method 3: Try searching by email field in all documents
      if (!userData) {
        try {
          const usersRef = collection(db, COLLECTIONS.USERS);
          const q = query(usersRef, where('email', '==', currentUser.email));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            userData = {
              id: userDoc.id, // Use the document ID from the query result
              email: currentUser.email,
              ...userDoc.data()
            };
            console.log('Found user data by email query:', userData);
          }
        } catch (error) {
          console.log('Error querying by email:', error);
        }
      }
      
      // Update current user if we found new data
      if (userData) {
        console.log('Updating current user with new data:', userData);
        setCurrentUser(userData);
        // שמור ב-localStorage
        saveUserToLocalStorage(userData);
        
        // Force a re-render by updating again after a short delay
        setTimeout(() => {
          setCurrentUser(prev => ({ ...userData }));
        }, 100);
      } else {
        console.log('No user data found');
      }
      
    } catch (error) {
      console.error('Error refreshing current user:', error);
    }
  };

  // Force refresh current user data from Firestore
  const forceRefreshCurrentUser = async () => {
    if (!currentUser?.email) return;
    
    try {
      console.log('Force refreshing current user data...');
      
      // Try to find user data by email
      const usersRef = collection(db, COLLECTIONS.USERS);
      const q = query(usersRef, where('email', '==', currentUser.email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = {
          id: userDoc.id,
          email: currentUser.email,
          ...userDoc.data()
        };
        
        console.log('Force refresh found user data:', userData);
        console.log('Current user data before update:', currentUser);
        
        // Update current user with new data
        setCurrentUser(userData);
        // שמור ב-localStorage
        saveUserToLocalStorage(userData);
        
        // Force a re-render
        setTimeout(() => {
          setCurrentUser(prev => {
            console.log('Force re-render with data:', userData);
            return { ...userData };
          });
        }, 100);
        
        return userData;
      } else {
        console.log('No user data found in force refresh');
      }
      
    } catch (error) {
      console.error('Error force refreshing current user:', error);
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true); // Always start with loading true
      
      if (user) {
        try {
          console.log('Auth state changed, loading user data...');
          
          // Try multiple approaches to find user data
          let userData = null;
          
          // Method 1: Try by email (formatted)
          const userDocId = user.email.replace(/[.@]/g, '_');
          try {
            const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userDocId));
            if (userDoc.exists()) {
              userData = {
                id: userDocId, // Use the document ID from Firestore
                email: user.email,
                ...userDoc.data()
              };
              console.log('Found user data by email:', userData);
            }
          } catch (error) {
            console.log('Error fetching by email:', error);
          }
          
          // Method 2: Try by UID if email didn't work
          if (!userData) {
            try {
              const userDocByUid = await getDoc(doc(db, COLLECTIONS.USERS, user.uid));
              if (userDocByUid.exists()) {
                userData = {
                  id: user.uid, // Use the UID
                  email: user.email,
                  ...userDocByUid.data()
                };
                console.log('Found user data by UID:', userData);
              }
            } catch (error) {
              console.log('Error fetching by UID:', error);
            }
          }
          
          // Method 3: Try searching by email field in all documents
          if (!userData) {
            try {
              const usersRef = collection(db, COLLECTIONS.USERS);
              const q = query(usersRef, where('email', '==', user.email));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                const userDoc = querySnapshot.docs[0];
                userData = {
                  id: userDoc.id, // Use the document ID from the query result
                  email: user.email,
                  ...userDoc.data()
                };
                console.log('Found user data by email query:', userData);
              }
            } catch (error) {
              console.log('Error querying by email:', error);
            }
          }

          // חיפוש ברשימת כל המשתמשים
if (!userData) {
  try {
    const allUsersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    for (const doc of allUsersSnapshot.docs) {
      const data = doc.data();
      if (data.email === user.email) {
        userData = {
          id: doc.id,
          email: user.email,
          name: data.name || user.displayName || user.email.split('@')[0],
          role: data.role || 'employee',
          departmentId: data.departmentId || '',
          teamId: data.teamId || ''
        };
        console.log('Found user in admin records:', userData);
        break;
      }
    }
  } catch (error) {
    console.log('Could not check admin records:', error);
  }
}
          
          // If still no data found, use default
          if (!userData) {
            userData = {
              id: user.uid,
              email: user.email,
              name: user.displayName || user.email.split('@')[0],
              role: 'employee',   // ברירת מחדל לעובד
              departmentId: ''
            };
            console.log('Using default user data:', userData);
          }

          // Ensure we have all required fields
          if (!userData.role) {
            userData.role = 'employee';   // ברירת מחדל לעובד
          }
          
          
          // שמור ב-localStorage
          saveUserToLocalStorage(userData);
          
          // Force a state update by creating a new object
          const finalUserData = { ...userData };
          setCurrentUser(finalUserData);
          
          // Ensure the update propagates by using a slight delay
          setTimeout(() => {
            setCurrentUser(prev => {
              return { ...finalUserData };
            });
          }, 100);
          
        } catch (error) {
          console.error('Error in auth state change:', error);
          const defaultUserData = {
            id: user.uid,
            email: user.email,
            name: user.displayName || user.email.split('@')[0],
            role: 'employee',   // ברירת מחדל לעובד
            departmentId: ''
          };
          setCurrentUser(defaultUserData);
          saveUserToLocalStorage(defaultUserData);
        }
      } else {
        setCurrentUser(null);
        // נקה את ה-localStorage כשאין משתמש מחובר
        localStorage.removeItem('userRole');
        localStorage.removeItem('userDepartmentId');
        localStorage.removeItem('userTeamId');
        localStorage.removeItem('userName');
        localStorage.removeItem('userEmail');
      }
      
      // Always set loading to false at the end
      setTimeout(() => {
        setLoading(false);
      }, 100);
    });

    return unsubscribe;
  }, []);

  // Add a real-time listener for user data changes
  useEffect(() => {
    if (!currentUser?.id) return;
    
    console.log('Setting up real-time listener for user:', currentUser.id);
    
    // Listen to real-time changes in the user document
    const userDocRef = doc(db, COLLECTIONS.USERS, currentUser.id);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const updatedUserData = {
          id: doc.id,
          email: currentUser.email, // Keep the email from auth
          ...doc.data()
        };
        
        
        // Only update if there are actual changes
        if (JSON.stringify(updatedUserData) !== JSON.stringify(currentUser)) {
          console.log('Updating current user with new data');
          setCurrentUser(updatedUserData);
          // עדכן גם ב-localStorage
          saveUserToLocalStorage(updatedUserData);
        } else {
          console.log('No changes detected, skipping update');
        }
      }
    }, (error) => {
      console.error('Error listening to user data changes:', error);
    });

    return unsubscribe;
  }, [currentUser?.id, currentUser?.email]);

    // Add a periodic refresh mechanism as a fallback
 /* useEffect(() => {
    if (!currentUser?.id) return;
    
    const interval = setInterval(async () => {
      try {
        console.log('Periodic refresh of user data...');
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.id));
        if (userDoc.exists()) {
          const updatedUserData = {
            id: userDoc.id,
            email: currentUser.email,
            ...userDoc.data()
          };
          
          // Only update if there are actual changes
          if (JSON.stringify(updatedUserData) !== JSON.stringify(currentUser)) {
            console.log('Periodic refresh found changes, updating user data');
            setCurrentUser(updatedUserData);
            // עדכן גם ב-localStorage
            saveUserToLocalStorage(updatedUserData);
          }
        }
      } catch (error) {
        console.error('Error in periodic refresh:', error);
      }
    }, 10000); // Check every 10 seconds for faster response
    
    return () => clearInterval(interval);
  }, [currentUser?.id, currentUser?.email]);*/

  const value = {
    currentUser,
    login,
    logout,
    hasPermission,
    loading,
    updateCurrentUser,
    updateCurrentUserLocal,
    refreshCurrentUser,
    forceRefreshCurrentUser,
    // Add a function to manually trigger a refresh
    manualRefresh: () => {
      if (currentUser?.id) {
        console.log('Manual refresh triggered');
        setCurrentUser(prev => ({ ...prev }));
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};